import { useState, useEffect, useMemo, useRef } from 'react';
import { MdAddCircleOutline, MdRemoveCircleOutline, MdOutlineFavorite } from 'react-icons/md';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { getProductsAlreadyOrdered, getProducts } from 'interfaces/products';

import Card from 'components/card';
import SearchIcon from 'components/icons/SearchIcon';

const AddProductsModal = (props: {
    isOpen: any;
    onClose: () => void;
    addProducts: (items: any[]) => void;
    existingProducts: string[];
    customerId?: string;
}) => {
    const { isOpen, onClose, addProducts, existingProducts, customerId } = props;

    const [search, setSearch] = useState('');
    const [tab, setTab] = useState<'all' | 'recent-order' | 'favoris'>('all');
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [recentIds, setRecentIds] = useState<Set<string>>(new Set());
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const loadProducts = async (page: number) => {
        if (isLoading) return;
        setIsLoading(true);
        const res = await getProducts(page, 100);
        if (res && res.data) {
            if (page === 1) {
                setProducts(res.data);
            } else {
                setProducts((prev) => [...prev, ...res.data]);
            }
            setHasMore(res.data.length === 100); // If we got 100, there might be more
        }
        setIsLoading(false);
    };

    const loadAllRemainingProducts = async () => {
        if (isLoading || !hasMore) return;
        setIsLoading(true);
        let page = currentPage + 1;
        let allNewProducts: any[] = [];
        let continueLoading = true;
        
        while (continueLoading) {
            const res = await getProducts(page, 100);
            if (res && res.data && res.data.length > 0) {
                allNewProducts = [...allNewProducts, ...res.data];
                continueLoading = res.data.length === 100;
                page++;
            } else {
                continueLoading = false;
            }
        }
        
        if (allNewProducts.length > 0) {
            setProducts((prev) => [...prev, ...allNewProducts]);
            setCurrentPage(page - 1);
        }
        setHasMore(false);
        setIsLoading(false);
    };

    useEffect(() => {
        if (isOpen) {
            setProducts([]);
            setCurrentPage(1);
            setHasMore(true);
            loadProducts(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight - 200 && hasMore && !isLoading) {
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                loadProducts(nextPage);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, hasMore, isLoading]);

    // Charger tous les produits restants quand une recherche est active
    useEffect(() => {
        if (search && search.length >= 2 && hasMore && !isLoading) {
            loadAllRemainingProducts();
        }
    }, [search]);

    const isSelected = (id: string) =>
        !!selectedProducts.find((p) => p.productId === id);

    useEffect(() => {
                if (!customerId || !isOpen) return;
                let cancelled = false;
                (async () => {
                    setLoadingRecent(true);
                    const res: any = await getProductsAlreadyOrdered(customerId);
                    if (!cancelled && Array.isArray(res)) {
                        setRecentIds(new Set(res.map((r: any) => r._id)));
                    }
                    setLoadingRecent(false);
                })();
                return () => {
                    cancelled = true;
                };
            }, [customerId, isOpen]);

    const enrichedProducts = useMemo(() => {
                if (!products) return [];
                if (recentIds.size === 0) return products;
                return products.map((p: any) => ({
                    ...p,
                    isRecent: p.isRecent || recentIds.has(p._id),
                 }));
    }, [products, recentIds]);

    const toggleSelect = (product: any) => {
        setSelectedProducts((prev) => {
            if (prev.find((p) => p.productId === product._id)) {
                return prev.filter((p) => p.productId !== product._id);
            }
            return [
                ...prev,
                {
                    ...product,
                    productId: product._id,
                    negotiatedPrice: product.price ?? 0,
                },
            ];
        });
    };

    const updateNegotiatedPrice = (productId: string, value: number) => {
        setSelectedProducts((prev) =>
            prev.map((p) =>
                p.productId === productId
                    ? { ...p, negotiatedPrice: value }
                    : p,
            ),
        );
    };

    const searchProduct = (product: any, search: string) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            (product.name || '').toLowerCase().includes(s) ||
            String(product.ref || '').toLowerCase().includes(s)
        );
    };

    const filteredProducts =
        enrichedProducts
            ?.filter((p: any) => searchProduct(p, search))
            .filter(
                (p: any) =>
                    !existingProducts.includes(p._id),
            )
            .filter((p: any) =>
                tab === 'recent-order'
                    ? p.isRecent
                    : tab === 'favoris'
                      ? p.isFavorite
                      : true,
            ) || [];

    const handleValidate = () => {

        const payload = selectedProducts.map((p) => ({
            ...p,
            price: p.negotiatedPrice,
        }));
        addProducts(payload);
        setSelectedProducts([]);
    };

    const getPurchasePrice = (p: any) =>
           Number(
             p?.purchasePrice ??
               p?.marginData?.purchasePrice ??
               p?.marginData?.purchase_price ??
               0,
           );
        
         const getMarginString = (purchase: number, sell: number) => {
           if (!purchase || !sell) return null;
           if (sell <= 0) return null;
           const diff = sell - purchase;
           const pct = (diff / sell) * 100;
           return `Marge : ${diff.toFixed(2).replace(/\.?0+$/,'')} € (${pct
             .toFixed(2)
             .replace(/\.?0+$/,'')} %)`;
         };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] mx-auto md:top-[12vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[780px] max-h-[560px] flexs !z-[1004] w-full mx-auto overflow-hidden flex flex-col">
                        <h1 className="text-lg font-bold">
                            Ajouter des produits
                        </h1>
                        <p className="mt-1 text-xs text-gray-600">
                            Cliquez pour sélectionner et saisir un prix négocié.
                        </p>

                        {/* Search */}
                        <div className="mt-3 flex h-[35px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder="Rechercher nom ou réf..."
                                onChange={(e) => setSearch(e.target.value)}
                                className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                            />
                        </div>

                        {/* Tabs */}
                        <div className="flex mt-3 gap-4">
                            <p
                                className={`text-xs ${
                                    tab === 'all'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/70'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('all')}
                            >
                                Tout
                            </p>
                            <p
                                className={`text-xs ${
                                    tab === 'recent-order'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/70'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('recent-order')}
                            >
                                Déjà commandé
                                {tab === 'recent-order' && loadingRecent && (
                                   <span className="animate-pulse text-[9px] text-gray-400">
                                       ...
                                   </span>
                               )}
                            </p>
                            {/* <p
                                className={`text-xs ${
                                    tab === 'favoris'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/70'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('favoris')}
                            >
                                Favoris
                            </p> */}
                        </div>

                        {/* Product list */}
                        <div ref={scrollContainerRef} className="overflow-y-auto flex-1 mt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {filteredProducts.length === 0 && !isLoading && (
                                <div className="col-span-full text-xs text-gray-500 px-2">
                                    {tab === 'recent-order'
                                        ? loadingRecent
                                            ? 'Chargement des produits déjà commandés...'
                                            : 'Aucun produit commandé trouvé'
                                        : 'Aucun produit'}
                                </div>
                            )}
                            {filteredProducts.map((item: any) => {
                                const selected = isSelected(item._id);
                                const sel = selectedProducts.find(
                                    (p) => p.productId === item._id,
                                );
                                const negotiated = sel?.negotiatedPrice ?? item.price ?? 0;
                                const purchasePrice = getPurchasePrice(item);
                                return (
                                    <div
                                        key={item._id}
                                        className={`relative flex flex-col gap-2 rounded-2xl border p-3 hover:cursor-pointer transition ${
                                            selected
                                                ? 'bg-green-50 border-green-400'
                                                : 'bg-gray-50 border-gray-200'
                                        }`}
                                        onClick={() => toggleSelect(item)}
                                    >
                                        <div className="flex justify-between">
                                            <p className="text-xs font-semibold text-navy-700 flex gap-2">
                                                {item.name || 'Inconnu'}
                                                {item.isFavorite && (
                                                    <MdOutlineFavorite
                                                        className="text-pink-500"
                                                        title="Favori"
                                                        size={14}
                                                    />
                                                )}
                                                {item.isRecent && !item.isFavorite && (
                                                    <MdOutlineFavorite
                                                        className="text-pink-400"
                                                        title="Déjà commandé"
                                                        size={14}
                                                    />
                                                )}
                                            </p>
                                        </div>
                                        <p className="text-[10px] text-gray-500 -mt-1">
                                                #{item.ref || '-'}
                                        </p>
                                        {!selected && (
                                            <p className="text-[11px] text-gray-600">
                                                Prix catalogue :{' '}
                                                <span className="font-medium">
                                                    {item.price ?? 0}€
                                                </span>
                                            </p>
                                        )}

                                        {selected && (
                                            <div
                                                className="flex flex-col gap-1"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <label className="text-[10px] font-medium text-gray-600">
                                                    Prix négocié (€)
                                                </label>
                                                <input
                                                    type="number"
                                                    className="h-[30px] text-xs rounded-lg border border-gray-300 px-2 outline-none focus:border-toola-500"
                                                    value={
                                                        sel?.negotiatedPrice ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        updateNegotiatedPrice(
                                                            item._id,
                                                            Number(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                />
                                                <p className="text-[12px] text-gray-600 mt-1 font-semibold">
                                                 {getMarginString(purchasePrice, negotiated) || 'Marge : -'}
                                               </p>
                                            </div>
                                        )}

                                        <div className="absolute top-1 right-1">
                                            {selected ? (
                                                <MdRemoveCircleOutline
                                                    size={20}
                                                    className="text-red-500"
                                                />
                                            ) : (
                                                <MdAddCircleOutline
                                                    size={20}
                                                    className="text-green-500"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {isLoading && (
                                <div className="col-span-full text-xs text-gray-500 px-2 py-4 text-center">
                                    Chargement...
                                </div>
                            )}
                        </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-6 flex gap-3 justify-end">
                            <button
                                className="text-xs px-4 py-2 rounded-xl border border-gray-300 hover:bg-gray-100"
                                onClick={() => {
                                    setSelectedProducts([]);
                                    onClose();
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                disabled={selectedProducts.length === 0}
                                className={`text-xs px-4 py-2 rounded-xl text-white font-medium ${
                                    selectedProducts.length === 0
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-toola-500 hover:bg-toola-600'
                                }`}
                                onClick={() => {
                                    handleValidate();
                                    onClose();
                                }}
                            >
                                Personnaliser les prix
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AddProductsModal;
